import React, { useState, useEffect } from 'react';
import { X, Eye, Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import { GrupoGuia } from '../types/grupos';
import { grupoGuiasService } from '../services/grupoGuias';
import { stripQuillColors } from '../lib/utils';

interface GuiaViewerProps {
  isOpen: boolean;
  onClose: () => void;
  guiaId: string | null;
}

export const GuiaViewer: React.FC<GuiaViewerProps> = ({ isOpen, onClose, guiaId }) => {
  const [guia, setGuia] = useState<GrupoGuia | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && guiaId) {
      loadGuia();
    }
  }, [isOpen, guiaId]);

  const loadGuia = async () => {
    if (!guiaId) return;

    try {
      setLoading(true);
      setError(null);

      // Carregar guia e incrementar visualização
      const guiaData = await grupoGuiasService.buscarPorId(guiaId, true);

      setGuia(guiaData);
    } catch (error) {
      console.error('Erro ao carregar guia:', error);
      setError('Erro ao carregar a guia');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseTags = (tags: string | undefined): string[] => {
    if (!tags) return [];
    try {
      return Array.isArray(tags) ? tags : JSON.parse(tags);
    } catch {
      return [];
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-4xl h-full max-h-[90vh] bg-background rounded-lg shadow-2xl flex flex-col border border-border">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-4">
              <button
                onClick={onClose}
                className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors"
                title="Voltar"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h2 className="text-xl font-semibold text-foreground">
                {loading ? 'Carregando...' : guia?.titulo || 'Guia'}
              </h2>
            </div>

            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-red-600 dark:text-red-400 mb-2">{error}</p>
                  <button
                    onClick={loadGuia}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            ) : guia ? (
              <div className="h-full overflow-auto">
                <div className="p-6">
                  {/* Meta informações */}
                  <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-muted-foreground border-b border-border pb-4">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      <span>{guia.militarCriador?.nick || 'Autor desconhecido'}</span>
                      {guia.militarCriador?.patente && (
                        <span className="text-muted-foreground/60">
                          ({guia.militarCriador.patente.patente})
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(guia.criado_em)}</span>
                    </div>

                    <div className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      <span>{guia.visualizacoes} visualizações</span>
                    </div>

                    {guia.categoria && (
                      <div className="flex items-center gap-1">
                        <Tag className="h-4 w-4" />
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 rounded-full text-xs">
                          {guia.categoria}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Descrição */}
                  {guia.descricao && (
                    <div className="mb-6">
                      <p className="text-foreground text-lg leading-relaxed">{guia.descricao}</p>
                    </div>
                  )}

                  {/* Tags */}
                  {parseTags(guia.tags).length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">Tags:</h4>
                      <div className="flex flex-wrap gap-2">
                        {parseTags(guia.tags).map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Conteúdo HTML */}
                  <div className="ql-snow" style={{ border: 'none' }}>
                    <div
                      className="ql-editor quill-display-content"
                      dangerouslySetInnerHTML={{ __html: stripQuillColors(guia.conteudo_html) }}
                      style={{
                        padding: 0,
                        border: 'none',
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: 'var(--foreground)',
                        background: 'transparent',
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-border bg-muted/50">
            <div className="text-sm text-muted-foreground">
              {guia && guia.alterado_em !== guia.criado_em && (
                <span>Última atualização: {formatDate(guia.alterado_em)}</span>
              )}
            </div>

            <button
              onClick={onClose}
              className="px-4 py-2 text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-muted transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
